import * as React from 'react'
import {
  useAddOwnVendorPhoto,
  useDeleteOwnVendorPhoto,
  useOwnVendorProfile,
  useReorderOwnVendorPhotos,
  useUpdateOwnVendorPhoto,
} from '@/hooks/useVendors'
import { requestErrorMessage } from '@/helpers/apiClient'
import { SpacePhotosField } from '@/components/SpacePhotosField'
import { AboutCard } from '@/components/vendor-profile/AboutCard'
import { ContactCard } from '@/components/vendor-profile/ContactCard'
import { MediaLinksCard } from '@/components/vendor-profile/MediaLinksCard'
import { OfferingsCard } from '@/components/vendor-profile/OfferingsCard'
import { ProfileChecklistCard } from '@/components/vendor-profile/ProfileChecklistCard'
import { PublicPageCard } from '@/components/vendor-profile/PublicPageCard'
import { useLocation } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { ErrorState, LoadingState } from '@/components/ui/states'
import { FormError } from '@/components/ui/form-error'
import { MAX_VENDOR_PHOTOS } from '@lib/constants/vendor-profile'
import type { OwnVendorProfileDto } from '@lib/dto/vendor-profile.dto'

/** The gallery: photos of the company's work, the first shown first. */
function PhotosCard({ profile }: { profile: OwnVendorProfileDto }) {
  const add = useAddOwnVendorPhoto()
  const update = useUpdateOwnVendorPhoto()
  const remove = useDeleteOwnVendorPhoto()
  const reorder = useReorderOwnVendorPhotos()
  const [error, setError] = React.useState<string | null>(null)
  const fail = (e: Error): void => setError(requestErrorMessage(e))
  const busy = add.isPending || update.isPending || remove.isPending || reorder.isPending

  return (
    <Card id="photos">
      <CardHeader>
        <CardTitle>Photos</CardTitle>
        <CardDescription>Your work, at its best. Planners choose vendors by what they see here.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <SpacePhotosField
          photos={profile.photos}
          canEdit
          busy={busy}
          max={MAX_VENDOR_PHOTOS}
          holder="Your gallery"
          emptyText="No photos yet. Add a few of your best events."
          // Awaited, so several photos dropped at once are added one after
          // another; a refusal stops the rest and the gallery says why.
          onAdd={async (image) => {
            setError(null)
            await add.mutateAsync({ image })
          }}
          onCaption={(photoId, caption) => {
            setError(null)
            update.mutate({ photoId, input: { caption } }, { onError: fail })
          }}
          onFocalPoint={(photoId, focalPoint) => {
            setError(null)
            update.mutate({ photoId, input: { focalPoint } }, { onError: fail })
          }}
          onRemove={(photoId) => {
            setError(null)
            remove.mutate(photoId, { onError: fail })
          }}
          onReorder={(photoIds) => {
            setError(null)
            reorder.mutate({ photoIds }, { onError: fail })
          }}
        />
        {error ? <FormError>{error}</FormError> : null}
      </CardContent>
    </Card>
  )
}

/**
 * The company's profile (docs/vendor-growth.spec.md): what venue staff and
 * planners see when they choose vendors. Everyone at the company edits it.
 */
export function VendorProfilePage() {
  const profile = useOwnVendorProfile()
  const location = useLocation()
  const loaded = profile.data !== undefined

  // A checklist step links to its card here; follow it once the cards exist.
  React.useEffect(() => {
    const id = decodeURIComponent(location.hash.replace(/^#/, ''))
    if (loaded && id !== '') document.getElementById(id)?.scrollIntoView({ block: 'start' })
  }, [location.hash, loaded])

  return (
    <PageContainer>
      <PageHeader title="Profile" meta="What venue staff and planners see about your business." />
      {profile.isPending ? <LoadingState label="Loading your profile…" /> : null}
      {profile.isError ? <ErrorState message={requestErrorMessage(profile.error)} onRetry={() => void profile.refetch()} /> : null}
      {profile.data ? (
        <div className="space-y-6">
          <ProfileChecklistCard />
          <PublicPageCard />
          <AboutCard profile={profile.data} />
          <ContactCard profile={profile.data} />
          <PhotosCard profile={profile.data} />
          <MediaLinksCard profile={profile.data} />
          <OfferingsCard profile={profile.data} />
        </div>
      ) : null}
    </PageContainer>
  )
}
